// src/hooks/useBooking.js
import { useState, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../config/firebase";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";
import { notifyStaffNewBooking } from "../utils/emailService";
import { occupiedSlots } from "../utils/slotUtils";
import { toDateStrInTz } from "../utils/dateUtils";

const INITIAL = {
  service: null, barber: null, date: null, time: null,
  customer: { name: "", email: "", phone: "", notes: "" },
};

// Fetch confirmed time slots for a given barber + date.
// Uses the publicly-readable /slots collection so any visitor can check availability.
export async function fetchBookedSlots(barberId, dateStr, excludeBookingId = null) {
  try {
    const q = query(
      collection(db, "slots"),
      where("barberId", "==", barberId),
      where("dateStr",  "==", dateStr),
      where("status",   "==", "confirmed")
    );
    const snap = await getDocs(q);
    // Doc ID: tenantId_barberId_dateStr_safeTime where safeTime = "10-00-AM"
    return snap.docs.map(d => {
      const parts = d.id.split("_");
      const safe  = parts[parts.length - 1]; // e.g. "10-00-AM"
      const t     = safe.split("-");          // ["10","00","AM"]
      return t.length >= 3 ? `${t[0]}:${t[1]} ${t[2]}` : null;
    }).filter(Boolean);
  } catch {
    return [];
  }
}

export function useBooking() {
  const { user }   = useAuth();
  const { tenant } = useTenant();
  const [booking,      setBooking]      = useState(INITIAL);
  const [step,         setStep]         = useState(1);
  const [confirmed,    setConfirmed]    = useState(false);
  const [emailSent,    setEmailSent]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingId,    setBookingId]    = useState(null);

  const updateBooking  = useCallback((f, v) => setBooking(p => ({ ...p, [f]: v })), []);
  const updateCustomer = useCallback((f, v) => setBooking(p => ({ ...p, customer: { ...p.customer, [f]: v } })), []);

  const prefillFromUser = useCallback(() => {
    if (user) {
      setBooking(p => ({
        ...p,
        customer: {
          ...p.customer,
          name:  p.customer.name  || user.displayName || "",
          email: p.customer.email || user.email || "",
        },
      }));
    }
  }, [user]);

  const canProceed = useCallback(() => {
    switch (step) {
      case 1: return !!booking.service;
      case 2: return !!booking.barber;
      case 3: return !!booking.date && !!booking.time;
      case 4:
        return (
          booking.customer.name.trim().length >= 2 &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(booking.customer.email)
        );
      default: return false;
    }
  }, [step, booking]);

  const confirmBooking = useCallback(async (paymentIntentId) => {
    setSaving(true);
    setBookingError("");
    try {
      const tz      = tenant?.timezone || "America/New_York";
      const dateStr = toDateStrInTz(booking.date, tz);
      const slug    = tenant?.slug || tenant?.id;

      const fn = httpsCallable(functions, "createBooking");
      const { data } = await fn({
        tenantId:    tenant?.id,
        barberId:    booking.barber.id,
        barberName:  booking.barber.name,
        dateStr,
        time:        booking.time,
        service:     booking.service,
        customer:    booking.customer,
        timezone:    tz,
        slug,
        userId:      user?.uid || null,
        paymentIntentId: paymentIntentId || null,
      });

      setBookingId(data.bookingId);
      setEmailSent(true);

      // Notify the staff member — fire and forget
      const staffMember = tenant?.staff?.find(s => s.id === booking.barber.id);
      if (staffMember?.email) {
        notifyStaffNewBooking(tenant?.id, staffMember.email, { ...booking, dateStr }, tenant);
      }
      setConfirmed(true);
    } catch (err) {
      const msg = (err.message || "") + (err.details || "");
      if (msg.includes("SLOT_TAKEN")) {
        setBookingError("This time slot was just taken by someone else. Please choose a different time.");
      } else if (msg.includes("DUPLICATE_BOOKING")) {
        setBookingError("You already have an appointment at this time. Please choose a different slot.");
      } else if (msg.includes("RATE_LIMITED")) {
        setBookingError("Too many booking attempts. Please wait a while before trying again.");
      } else if (msg.includes("BOOKING_LIMIT_REACHED")) {
        setBookingError("This business has reached its monthly booking limit. Please contact them directly.");
      } else if (msg.includes("CLOSED_DAY")) {
        setBookingError("This business is closed on the selected day. Please choose an open day.");
      } else if (msg.includes("OUTSIDE_HOURS")) {
        setBookingError("That time is outside the business's opening hours. Please choose a time within their hours.");
      } else if (msg.includes("PAYMENT_ALREADY_USED")) {
        setBookingError("This payment was already used for another booking. Please refresh and try again.");
      } else if (msg.includes("PAYMENT_REQUIRED") || msg.includes("PAYMENT_NOT_VERIFIED") || msg.includes("PAYMENT_VERIFICATION_FAILED")) {
        setBookingError("We couldn't verify your payment. Please try the payment step again.");
      } else {
        console.error("Booking error:", err);
        setBookingError("Something went wrong saving your booking. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }, [booking, user, tenant]);

  const reset = useCallback(() => {
    setBooking(INITIAL);
    setStep(1);
    setConfirmed(false);
    setEmailSent(false);
    setBookingError("");
    setBookingId(null);
  }, []);

  return {
    booking, bookingId, step, confirmed, emailSent, saving, bookingError,
    setStep, updateBooking, updateCustomer,
    prefillFromUser, canProceed, confirmBooking, reset,
  };
}
